/* Client-side renderer for Shopify rich_text_field values embedded in
   metaobjects. Liquid returns metaobject rich_text fields as JSON
   strings (not Hashes), so server-side iteration isn't possible — we
   stash the JSON on a data attribute and parse it here, walking the
   AST into HTML.

   Markup contract (set up in snippets/oddit-richtext-render.liquid):
     <span data-oddit-richtext
           data-oddit-richtext-source='{"type":"root","children":[...]}'>
     </span>

   defensive parsing. Some Shopify metaobject Drops serialize
   via `| json` with PascalCase keys ("Type"/"Children") and Python-
   style booleans ("True"). We coerce the source to standard JSON
   before parsing AND look up node properties case-insensitively so
   either shape renders correctly. */
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* coerce non-standard JSON variants emitted by Shopify metaobject
     Drops into standard JSON before parsing.

     Three shapes we've seen in production:
       a) Ruby Hash inspect:   {"type"=>"root","children"=>[…]}
          (when the Drop is rendered via to_s — uses => instead of :)
       b) Python-style booleans:  {"bold": True, "italic": False, "x": None}
       c) PascalCase keys:        {"Type":"Root","Children":[…]}
          (PascalCase is fine for JSON.parse — our renderer does a
          case-insensitive prop lookup so no normalization needed.)

     This function fixes (a) and (b). The renderer handles (c). The Ruby
     arrow replacement runs first because once `=>` is in place no
     keyword normalization can match. */
  function normalizeJson(raw) {
    return raw
      /* Ruby hash arrows → JSON colons. Match `"key" =>` so we don't
         accidentally rewrite literal `=>` inside a quoted text value
         (text values come after a `:` not after a `"`+arrow). */
      .replace(/"\s*=>\s*/g, '":')
      .replace(/:\s*True\b/g, ': true')
      .replace(/:\s*False\b/g, ': false')
      .replace(/:\s*None\b/g, ': null');
  }

  /* TL-132: case-insensitive property lookup. Some serializers emit
     "Type"/"Children"/"Value"/"Bold", others "type"/"children"/etc. */
  function prop(node, name) {
    if (!node || typeof node !== 'object') return undefined;
    if (node[name] !== undefined) return node[name];
    var upper = name.charAt(0).toUpperCase() + name.slice(1);
    return node[upper];
  }

  function renderChildren(children) {
    if (!Array.isArray(children)) return '';
    var html = '';
    for (var i = 0; i < children.length; i++) {
      html += renderNode(children[i]);
    }
    return html;
  }

  var inlineMode = false;

  function renderNode(node) {
    if (!node || typeof node !== 'object') return '';
    var type = String(prop(node, 'type') || '').toLowerCase();
    var children = prop(node, 'children');
    switch (type) {
      case 'root':
        return renderChildren(children);
      case 'paragraph':
        return inlineMode
          ? renderChildren(children)
          : '<p>' + renderChildren(children) + '</p>';
      case 'heading': {
        var level = Math.min(6, Math.max(1, Number(prop(node, 'level')) || 2));
        return '<h' + level + '>' + renderChildren(children) + '</h' + level + '>';
      }
      case 'list': {
        var tag = String(prop(node, 'listType') || '').toLowerCase() === 'ordered' ? 'ol' : 'ul';
        return '<' + tag + '>' + renderChildren(children) + '</' + tag + '>';
      }
      case 'list-item':
        return '<li>' + renderChildren(children) + '</li>';
      case 'link': {
        var url = prop(node, 'url');
        var href = url ? escapeHtml(url) : '#';
        return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + renderChildren(children) + '</a>';
      }
      case 'text': {
        var value = escapeHtml(prop(node, 'value') || '');
        if (prop(node, 'bold')) value = '<strong>' + value + '</strong>';
        if (prop(node, 'italic')) value = '<em>' + value + '</em>';
        return value;
      }
      default:
        return renderChildren(children);
    }
  }

  function readSource(container) {
    /* TL-132: prefer the data-* attribute (current contract) and fall
       back to a child <script type="application/json"> for any cached
       output still on the page from the previous snippet revision. */
    var attr = container.getAttribute('data-oddit-richtext-source');
    if (attr) return attr.trim();
    var script = container.querySelector('script[type="application/json"][data-oddit-richtext-source]');
    if (script) {
      var s = (script.textContent || '').trim();
      container.removeChild(script);
      return s;
    }
    return '';
  }

  function hydrate(container) {
    var raw = readSource(container);
    if (!raw || raw === 'null' || raw === '""') {
      container.removeAttribute('data-oddit-richtext-source');
      return;
    }
    /* TL-132: log what came through so the merchant can copy/paste it
       to us if the heading stays empty. Cheap diagnostic — only fires
       once per failed render, never in the success path. */
    function bail(reason) {
      try {
        console.warn('[oddit-richtext] could not render — ' + reason +
          '. Raw source was:', raw);
      } catch (_) {}
      container.removeAttribute('data-oddit-richtext-source');
    }
    var data;
    try {
      data = JSON.parse(normalizeJson(raw));
    } catch (e) {
      /* The raw payload looked like HTML rather than JSON — that's the
         shape Shopify emits when a richtext field is rendered server-
         side via to_s. Inject it directly. Anything else is a real
         parse failure and gets stripped. */
      var trimmed = raw.replace(/^\s+/, '');
      if (trimmed.charAt(0) === '<') {
        inlineMode = container.hasAttribute('data-oddit-richtext-inline');
        if (inlineMode) {
          /* Strip a single outer <p>…</p> so the heading doesn't end
             up with a paragraph inside it. */
          var unwrapped = trimmed.replace(/^<p>([\s\S]*)<\/p>\s*$/i, '$1');
          container.innerHTML = unwrapped;
        } else {
          container.innerHTML = trimmed;
        }
        container.removeAttribute('data-oddit-richtext-source');
        return;
      }
      bail('JSON.parse failed (' + (e && e.message) + ')');
      return;
    }
    if (typeof data === 'string') {
      try { data = JSON.parse(normalizeJson(data)); } catch (e) { /* leave */ }
    }
    inlineMode = container.hasAttribute('data-oddit-richtext-inline');
    var html = '';
    if (data && typeof data === 'object') {
      html = renderNode(data);
    } else if (typeof data === 'string') {
      html = inlineMode ? escapeHtml(data) : '<p>' + escapeHtml(data) + '</p>';
    }
    if (!html) {
      bail('renderer produced empty output (likely unknown node types)');
      return;
    }
    container.innerHTML = html;
    container.removeAttribute('data-oddit-richtext-source');
  }

  function hydrateAll(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-oddit-richtext]');
    for (var i = 0; i < nodes.length; i++) hydrate(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hydrateAll(); });
  } else {
    hydrateAll();
  }

  // Expose so sections that re-render dynamically (cart drawer, etc.)
  // can hydrate newly inserted nodes.
  window.OdditRichText = { hydrate: hydrate, hydrateAll: hydrateAll };
})();
